export interface RateLimitConfig {
  windowMs: number;
  maxRequests: number;
}

export class RateLimiter {
  private buckets: Map<string, { count: number; windowStart: number }> = new Map();
  private config: RateLimitConfig;

  constructor(config?: Partial<RateLimitConfig>) {
    this.config = {
      windowMs: config?.windowMs ?? 1000,
      maxRequests: config?.maxRequests ?? 10,
    };
  }

  allow(key: string): boolean {
    this.pruneExpired();

    const now = Date.now();
    const bucket = this.buckets.get(key);

    if (!bucket || now - bucket.windowStart >= this.config.windowMs) {
      this.buckets.set(key, { count: 1, windowStart: now });
      return true;
    }

    if (bucket.count >= this.config.maxRequests) {
      return false;
    }

    bucket.count++;
    return true;
  }

  remaining(key: string): number {
    const bucket = this.buckets.get(key);
    if (!bucket) return this.config.maxRequests;

    if (Date.now() - bucket.windowStart >= this.config.windowMs) {
      return this.config.maxRequests;
    }

    return Math.max(0, this.config.maxRequests - bucket.count);
  }

  /** Milissegundos restantes até a janela atual expirar (0 se não houver bucket ou janela expirada). */
  timeUntilReset(key: string): number {
    const bucket = this.buckets.get(key);
    if (!bucket) return 0;

    const elapsed = Date.now() - bucket.windowStart;
    if (elapsed >= this.config.windowMs) return 0;

    return this.config.windowMs - elapsed;
  }

  reset(key?: string): void {
    if (key) {
      this.buckets.delete(key);
    } else {
      this.buckets.clear();
    }
  }

  getConfig(): RateLimitConfig {
    return { ...this.config };
  }

  private pruneExpired(): void {
    const now = Date.now();
    for (const [key, bucket] of this.buckets) {
      if (now - bucket.windowStart >= this.config.windowMs) {
        this.buckets.delete(key);
      }
    }
  }
}
